import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Upload, Trash2, Download, FileText, Image as ImageIcon, Search, Music, Video, Loader2, Sparkles, Tag, MessageCircle } from "lucide-react";

const KINDS = [
  { k: "", label: "All" },
  { k: "image", label: "Images" },
  { k: "document", label: "Docs" },
  { k: "audio", label: "Audio" },
  { k: "video", label: "Video" },
  { k: "logo", label: "Logos" },
];

function KindIcon({ kind }) {
  if (kind === "image" || kind === "logo") return <ImageIcon className="h-5 w-5 text-[#075E54]" />;
  if (kind === "audio") return <Music className="h-5 w-5 text-[#075E54]" />;
  if (kind === "video") return <Video className="h-5 w-5 text-pink-400" />;
  return <FileText className="h-5 w-5 text-[#54656F]" />;
}

export default function AssetLibrary() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("");
  const [sort, setSort] = useState("recent");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/assets", { params: { search, kind, sort } });
      setAssets(r.data);
    } catch (e) {}
  }, [search, kind, sort]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); load(); }
    catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (id) => { await api.delete(`/assets/${id}`); load(); toast.success("Removed"); };
  const [tagging, setTagging] = useState(false);
  const autoTagAll = async () => {
    setTagging(true);
    try { const r = await api.post("/assets/auto-tag-all"); toast.success(`AI tagged ${r.data.tagged} assets`); load(); }
    catch (e) { toast.error("Auto-tag failed"); } finally { setTagging(false); }
  };
  const autoTagOne = async (id) => {
    try { await api.post(`/assets/${id}/auto-tag`); toast.success("Tagged"); load(); }
    catch (e) { toast.error("Tag failed"); }
  };

  const fileUrl = (a) => `${API_BASE}/assets/file/${a.storage_path}`;

  return (
    <>
      <PageHeader eyebrow="Media" title="Asset Library"
        actions={
          <>
            <button data-testid="asset-autotag-all" onClick={autoTagAll} disabled={tagging}
              className="flex items-center gap-2 bg-[#E7FCE8] text-[#128C7E] border border-[#25D366]/30 hover:bg-[#DCF8C6] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
              {tagging ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI Auto-tag
            </button>
            <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid="asset-file-input" />
            <button data-testid="asset-upload" onClick={() => fileRef.current?.click()} disabled={busy}
              className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-white rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
            </button>
          </>
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 space-y-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-[#8696A0] absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="asset-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search filename…"
              className="w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] rounded-lg pl-9 pr-3 py-2 text-sm text-[#111B21] focus:outline-none" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {KINDS.map((k) => (
              <button key={k.k} data-testid={`asset-kind-${k.k || "all"}`} onClick={() => setKind(k.k)}
                className={`text-xs px-3 py-2 rounded-lg border transition-colors ${kind === k.k ? "bg-[#F0F2F5] text-[#111B21] border-[#CBD5E1]" : "border-[#E9EDEF] text-[#8696A0] hover:text-[#374151]"}`}>{k.label}</button>
            ))}
          </div>
          <select data-testid="asset-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="bg-white border border-[#E9EDEF] rounded-lg px-3 py-2 text-sm text-[#111B21] focus:outline-none">
            <option value="recent">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="size">Largest</option>
          </select>
        </div>

        {assets.length === 0 ? (
          <div className="text-center py-16 text-sm text-[#8696A0]">No assets yet. Upload files or they'll appear here from WhatsApp chats.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="asset-grid">
            {assets.map((a) => (
              <div key={a.asset_id} className="border border-[#E9EDEF] bg-white rounded-lg overflow-hidden group">
                <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                  {a.kind === "image" || a.kind === "logo" ? (
                    <img src={fileUrl(a)} alt={a.original_filename} className="w-full h-full object-cover" />
                  ) : (<KindIcon kind={a.kind} />)}
                </div>
                <div className="p-2">
                  <p className="text-xs text-[#374151] truncate" title={a.original_filename}>{a.original_filename}</p>
                  {a.category && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="font-mono text-[8px] uppercase px-1.5 py-0.5 rounded-lg bg-[#E7FCE8] text-[#128C7E] border border-[#25D366]/30">{a.category}</span>
                      {(a.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="font-mono text-[8px] px-1.5 py-0.5 rounded-lg bg-[#F0F2F5] text-[#54656F] border border-[#CBD5E1]">{t}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="font-mono text-[9px] text-[#8696A0] uppercase">{a.source}</span>
                    <div className="flex gap-1.5">
                      {a.conversation_id && (
                        <button onClick={() => navigate(`/inbox?conversation=${a.conversation_id}`)}
                          data-testid={`asset-openchat-${a.asset_id}`} title={`Open chat${a.contact_name ? " with " + a.contact_name : ""}`}
                          className="text-[#8696A0] hover:text-[#25D366]"><MessageCircle className="h-3.5 w-3.5" /></button>
                      )}
                      <button onClick={() => autoTagOne(a.asset_id)} data-testid={`asset-tag-${a.asset_id}`} title="AI tag" className="text-[#8696A0] hover:text-[#075E54]"><Tag className="h-3.5 w-3.5" /></button>
                      <a href={fileUrl(a)} download={a.original_filename} target="_blank" rel="noreferrer"
                        data-testid={`asset-download-${a.asset_id}`} className="text-[#8696A0] hover:text-[#075E54]"><Download className="h-3.5 w-3.5" /></a>
                      <button onClick={() => del(a.asset_id)} data-testid={`asset-delete-${a.asset_id}`} className="text-[#8696A0] hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  {a.conversation_id && (
                    <button onClick={() => navigate(`/inbox?conversation=${a.conversation_id}`)}
                      className="mt-1 w-full flex items-center gap-1 text-[9px] text-[#128C7E] hover:underline truncate" title="Saved from this chat">
                      <MessageCircle className="h-3 w-3 shrink-0" />
                      <span className="truncate">from {a.contact_name || "chat"}</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
